import { Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  ChartLineUp, Users, Receipt, Calculator, ChartBar, CalendarBlank, Pulse,
  SignOut, HandCoins, ChatCircle, PlusCircle, PlugsConnected,
  List, X as IconX, ShieldCheck, Key, Lightning, Clock, EnvelopeSimple,
  Trash, CloudArrowUp, IdentificationBadge,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n";
import { BrandLockup, Footer } from "@/components/Brand";
import api, { formatApiError } from "@/lib/api";
import useSmartPoll from "@/lib/useSmartPoll";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { corporateInput } from "@/components/Shared";
import { toast } from "sonner";

// Build nav groups dynamically so labels react to language changes.
function useNavGroups(t, pendingReconnect = 0, counts = {}) {
  return [
    {
      label: t("nav.group.panel", "Panel"),
      items: [{ to: "/", label: t("nav.dashboard"), icon: ChartLineUp, testid: "nav-dashboard" }],
    },
    {
      label: t("nav.group.operation"),
      items: [
        { to: "/conexion-nueva", label: t("nav.newConnection"), icon: PlusCircle, testid: "nav-new-connection", highlight: true },
        { to: "/calendario", label: t("nav.calendar"), icon: CalendarBlank, testid: "nav-calendar" },
      ],
    },
    {
      label: t("nav.group.clients", "Clientes"),
      items: [
        { to: "/clientes", label: t("nav.clients"), icon: Users, testid: "nav-clients" },
        { to: "/clientes/pendientes", label: t("nav.clients.pending", "Pendientes de activación"), icon: Clock, testid: "nav-clients-pending-activation", badgeCount: counts.pending_activation || 0, badgeTone: "amber" },
        { to: "/clientes/reconexion", label: t("nav.clients.reconnect"), icon: PlugsConnected, testid: "nav-clients-reconnect", badgeCount: pendingReconnect || counts.pending_reconnection || 0, badgeTone: "indigo" },
        { to: "/clientes/desconectados", label: t("nav.clients.disconnected"), icon: PlugsConnected, testid: "nav-clients-disconnected", badgeCount: counts.disconnected_clients || 0, badgeTone: "rose" },
        { to: "/papelera", label: "Papelera", icon: Trash, testid: "nav-trash" },
      ],
    },
    {
      label: t("nav.group.finance"),
      items: [
        { to: "/facturas", label: t("nav.invoices"), icon: Receipt, testid: "nav-invoices" },
        { to: "/contabilidad", label: t("nav.accounting"), icon: Calculator, testid: "nav-accounting" },
        { to: "/reportes", label: t("nav.reports"), icon: ChartBar, testid: "nav-reports" },
        { to: "/payouts", label: t("nav.payouts"), icon: HandCoins, testid: "nav-payouts" },
        { to: "/planes", label: t("nav.plans"), icon: Lightning, testid: "nav-plans" },
      ],
    },
    {
      label: t("nav.group.communication"),
      items: [
        { to: "/comunicacion", label: t("nav.communications"), icon: ChatCircle, testid: "nav-comms", badgeKey: "unread_messages" },
        { to: "/correos", label: "Historial de correos", icon: EnvelopeSimple, testid: "nav-email-log" },
      ],
    },
    {
      label: t("nav.group.admin", "Administración"),
      items: [
        { to: "/admins", label: t("nav.admins"), icon: ShieldCheck, testid: "nav-admins" },
        { to: "/agentes", label: "Agentes / Referidos", icon: IdentificationBadge, testid: "nav-agents" },
        { to: "/respaldos", label: "Respaldos", icon: CloudArrowUp, testid: "nav-backups" },
        { to: "/actividad", label: t("nav.activity"), icon: Pulse, testid: "nav-activity" },
      ],
    },
  ];
}

export default function Layout({ children }) {
  const { pathname } = useLocation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [unread, setUnread] = useState({ unread_messages: 0 });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [pwdSaving, setPwdSaving] = useState(false);

  const submitPasswordChange = async () => {
    if (pwdForm.new_password !== pwdForm.confirm) {
      toast.error(t("pwd.mismatch", "Las contraseñas no coinciden"));
      return;
    }
    if (pwdForm.new_password.length < 8) {
      toast.error(t("pwd.tooShort", "Mínimo 8 caracteres"));
      return;
    }
    setPwdSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pwdForm.current_password,
        new_password: pwdForm.new_password,
      });
      toast.success(t("pwd.success", "Contraseña actualizada"));
      setPwdOpen(false);
      setPwdForm({ current_password: "", new_password: "", confirm: "" });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setPwdSaving(false); }
  };

  const isLimited = user?.role === "limited_admin";
  const isCS = user?.role === "customer_service";
  const isAccountant = user?.role === "admin";
  const isSuper = user?.role === "super_admin";
  const [pendingReconnect, setPendingReconnect] = useState(0);
  const [counts, setCounts] = useState({ pending_activation: 0, pending_reconnection: 0, disconnected_clients: 0, critical_clients: 0, outages: 0 });

  // Route-based hiding by role
  // - Full-admin/super only: /admins, /respaldos, /seguridad
  // - Finance-only (contable + super): /planes, /contabilidad, /reportes, /payouts, /agentes
  // - Service-only (servicio + super, no contable): /conexion-nueva
  const FULL_ADMIN_ONLY = ["/admins", "/respaldos"];
  const FINANCE_ONLY = ["/planes", "/contabilidad", "/reportes", "/payouts", "/agentes"];
  const SERVICE_ONLY = ["/conexion-nueva"];

  const shouldHide = (to) => {
    if (isSuper) return false;
    if (FULL_ADMIN_ONLY.includes(to) && !(isAccountant || isSuper)) return true;
    if (FINANCE_ONLY.includes(to) && isCS) return true;
    if (SERVICE_ONLY.includes(to) && isAccountant) return true;
    if (isLimited && ["/admins", "/respaldos", "/seguridad"].includes(to)) return true;
    return false;
  };

  const navGroups = useNavGroups(t, pendingReconnect, counts).map(g => ({
    ...g,
    items: g.items.filter(it => !shouldHide(it.to)),
  })).filter(g => g.items.length > 0);

  useSmartPoll(() => {
    // Prefer new PRO polling endpoint (returns unread + mentions in one shot)
    api.get("/threads/updates/poll")
      .then((r) => setUnread({ unread_messages: r.data?.total_unread || 0, mentions_unread: r.data?.mentions_unread || 0 }))
      .catch(() => {
        // Fallback to legacy summary endpoint if PRO endpoint not deployed yet
        api.get("/threads/summary/unread")
          .then((r) => setUnread({ unread_messages: r.data?.unread_messages || 0 }))
          .catch(() => setUnread({ unread_messages: 0 }));
      });
    api.get("/clients/pending-reconnection/count")
      .then((r) => setPendingReconnect(r.data?.count || 0))
      .catch(() => setPendingReconnect(0));
    api.get("/clients/sidebar-counts")
      .then((r) => setCounts(r.data || {}))
      .catch(() => {});
  }, 15000);

  // Close drawer on navigation
  useEffect(() => { setDrawerOpen(false); }, [pathname]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const SidebarContent = (
    <>
      <div className="px-5 h-16 flex items-center justify-between border-b border-white/10">
        <BrandLockup size={26} variant="dark" subtitle={t("brand.financial")} />
        <button
          onClick={() => setDrawerOpen(false)}
          className="lg:hidden text-slate-300 hover:text-white"
          aria-label="Cerrar menú"
          data-testid="sidebar-close"
        >
          <IconX size={18} />
        </button>
      </div>

      <nav className="flex-1 min-h-0 px-3 py-5 space-y-6 overflow-y-auto sidebar-scroll">
        {navGroups.map((group) => (
          <div key={group.label}>
            <div className="px-3 mb-1.5 text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold">
              {group.label}
            </div>
            <div>
              {group.items.map(({ to, label, icon: Icon, testid, badgeKey, badgeCount, badgeTone, highlight }) => {
                const active = pathname === to;
                const badgeValue = badgeKey ? unread[badgeKey] : (badgeCount || 0);
                const toneClass = badgeTone === "rose"
                  ? "bg-rose-500 text-white"
                  : badgeTone === "indigo"
                    ? "bg-indigo-400 text-slate-900"
                    : badgeTone === "amber"
                      ? "bg-amber-400 text-slate-900"
                      : "bg-amber-400 text-slate-900";
                return (
                  <Link
                    key={to}
                    to={to}
                    data-testid={testid}
                    className={`relative flex items-center gap-3 px-3 py-2 text-[13px] font-medium transition-colors ${
                      active
                        ? "text-white bg-white/[0.06]"
                        : "text-slate-300 hover:text-white hover:bg-white/[0.04]"
                    }`}
                  >
                    {active && (
                      <span className="absolute left-0 top-1.5 bottom-1.5 w-[2px] bg-white" />
                    )}
                    <Icon size={16} weight="regular" className={active ? "text-white" : "text-slate-400"} />
                    <span className="flex-1 truncate">{label}</span>
                    {highlight && !active && (
                      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-300">●</span>
                    )}
                    {badgeValue > 0 && (
                      <span className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-semibold rounded-full ${toneClass}`} data-testid={`${testid}-badge`}>{badgeValue}</span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}

        {/* Session block — directly below last sidebar item */}
        <div>
          <div className="px-3 mb-1.5 text-[10px] uppercase tracking-[0.22em] text-slate-400 font-semibold">{t("nav.group.session")}</div>
          <div className="px-3 py-1 mb-1">
            <div className="text-xs font-medium text-slate-100 truncate" data-testid="user-email">{user?.email}</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-400 font-semibold mt-0.5" data-testid="user-role-label">
              {user?.role === "super_admin" && "Super Admin"}
              {user?.role === "admin" && "Contable"}
              {user?.role === "customer_service" && "Servicio al Cliente"}
              {user?.role === "limited_admin" && "Admin (lectura)"}
            </div>
          </div>
          <Link
            to="/seguridad"
            data-testid="security-link"
            className="w-full flex items-center gap-3 px-3 py-2 text-[13px] font-medium text-slate-300 hover:bg-white/[0.04] hover:text-white transition-colors"
          >
            <Key size={16} className="text-slate-400" />
            <span className="flex-1 text-left">{t("nav.security", "Seguridad")}</span>
          </Link>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center gap-3 px-3 py-2 text-[13px] font-medium text-slate-300 hover:bg-white/[0.04] hover:text-white transition-colors"
          >
            <SignOut size={16} className="text-slate-400" />
            <span className="flex-1 text-left">{t("nav.logout")}</span>
          </button>
        </div>
      </nav>
    </>
  );

  return (
    <div className="min-h-screen flex bg-slate-50 dark:bg-slate-950 transition-colors">
      {/* Static sidebar on desktop — sticky, own scroll */}
      <aside
        className="w-60 hidden lg:flex flex-col shrink-0 text-slate-300 border-r border-slate-800 sticky top-0 h-screen"
        style={{ background: "#0A2540" }}
        data-testid="sidebar"
      >
        {SidebarContent}
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" data-testid="sidebar-drawer">
          <div className="w-60 h-screen flex flex-col text-slate-300 border-r border-slate-800 animate-in slide-in-from-left duration-200" style={{ background: "#0A2540" }}>
            {SidebarContent}
          </div>
          <div className="flex-1 bg-slate-950/60 backdrop-blur-sm" onClick={() => setDrawerOpen(false)} />
        </div>
      )}

      <main className="flex-1 min-w-0 flex flex-col">
        {/* Mobile top bar */}
        <div className="lg:hidden h-14 flex items-center justify-between px-4 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 transition-colors">
          <button
            onClick={() => setDrawerOpen(true)}
            className="text-slate-700 dark:text-slate-300 p-1.5 -ml-1.5"
            data-testid="open-sidebar"
            aria-label="Abrir menú"
          >
            <List size={20} />
          </button>
          <BrandLockup size={20} subtitle={t("brand.financial")} />
          <div className="w-7" />
        </div>

        <div className="flex-1">{children}</div>
        <Footer />
      </main>

      {/* Change password dialog */}
      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{t("pwd.title", "Cambiar contraseña")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{t("pwd.current", "Contraseña actual")} *</Label>
              <PasswordInput autoComplete="current-password" value={pwdForm.current_password} onChange={(e) => setPwdForm({ ...pwdForm, current_password: e.target.value })} className={corporateInput} data-testid="pwd-current" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{t("pwd.new", "Nueva contraseña")} *</Label>
              <PasswordInput autoComplete="new-password" value={pwdForm.new_password} onChange={(e) => setPwdForm({ ...pwdForm, new_password: e.target.value })} className={corporateInput} data-testid="pwd-new" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-semibold uppercase tracking-[0.18em] text-slate-500">{t("pwd.confirm", "Confirmar nueva")} *</Label>
              <PasswordInput autoComplete="new-password" value={pwdForm.confirm} onChange={(e) => setPwdForm({ ...pwdForm, confirm: e.target.value })} className={corporateInput} data-testid="pwd-confirm" />
            </div>
            <p className="text-[10px] text-slate-500">{t("pwd.hint", "Mínimo 8 caracteres. Diferente a la actual.")}</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdOpen(false)} disabled={pwdSaving}>{t("common.cancel")}</Button>
            <Button onClick={submitPasswordChange} disabled={pwdSaving || !pwdForm.current_password || !pwdForm.new_password} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="pwd-submit">
              {pwdSaving ? t("common.saving") : t("pwd.submit", "Actualizar")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
